import api from '../utils/api';
import { getApiPrefix } from './session';
import { filterSubscriptionCustomers } from './userDisplay';

function prefix(path = '') {
  const base = getApiPrefix().replace(/\/$/, '');
  const segment = path.startsWith('/') ? path : `/${path}`;
  return `${base}${segment}`;
}

function unwrapResponse(res) {
  const body = res?.data;
  if (body && body.success === false) {
    const err = new Error(body.message || 'Request failed');
    err.status = res.status;
    err.response = res;
    throw err;
  }
  if (body && Object.prototype.hasOwnProperty.call(body, 'data')) {
    return body.data;
  }
  return body;
}

async function request(method, path, options = {}) {
  const { params, data, headers } = options;
  const res = await api.request({
    method,
    url: prefix(path),
    params,
    data,
    headers,
  });
  return unwrapResponse(res);
}

/** Step 0 — legacy user APIs (same Bearer token, no subscription-admin prefix). */
export const legacyCustomerApi = {
  listUsers: async () => {
    const res = await api.get('/user-details');
    const body = res?.data;
    return Array.isArray(body) ? body : body?.data || [];
  },

  createCustomer: async (payload) => {
    const res = await api.post('/register-user', payload);
    return res?.data;
  },

  updateCustomer: async (userId, payload) => {
    const res = await api.put(`/putuser/${userId}`, payload);
    return res?.data;
  },
};

export { filterSubscriptionCustomers };

export const subscriptionAdminApi = {
  health: () => request('GET', '/health'),
  authInfo: () => request('GET', '/auth'),

  // Step 1 — Plans
  listPlans: (params) => request('GET', '/plans-list', { params }),
  getPlan: (id) => request('GET', `/plans-get/${id}`),
  createPlan: (body) => request('POST', '/plans-create', { data: body }),
  updatePlan: (id, body) => request('PUT', `/plans-put/${id}`, { data: body }),
  setPlanStatus: (id, status) =>
    request('PATCH', `/plans-patch/${id}/status`, { data: { status } }),
  deletePlan: (id) => request('DELETE', `/plans-delete/${id}`),

  // Step 2 — Contracts
  listContracts: (params) => request('GET', '/contracts-list', { params }),
  getContract: (id) => request('GET', `/contracts-get/${id}`),
  createContract: (body) => request('POST', '/contracts-create', { data: body }),
  updateContract: (id, body) => request('PUT', `/contracts-put/${id}`, { data: body }),
  submitContract: (id) => request('POST', `/contracts-submit/${id}`),
  approveContract: (id) => request('POST', `/contracts-approve/${id}`),
  sendContractForPayment: (id) => request('POST', `/contracts-send-for-payment/${id}`),
  activateContract: (id) => request('POST', `/contracts-activate/${id}`),
  activateContractOffline: (id, body) =>
    request('POST', `/contracts-activate-offline/${id}`, { data: body }),
  recordContractPayment: (id, body) =>
    request('POST', `/contracts-record-payment/${id}`, { data: body }),
  suspendContract: (id, body) => request('POST', `/contracts-suspend/${id}`, { data: body }),
  resumeContract: (id) => request('POST', `/contracts-resume/${id}`),
  renewContract: (id) => request('POST', `/contracts-renew/${id}`),
  terminateContract: (id) => request('POST', `/contracts-terminate/${id}`),
  cancelContract: (id) => request('POST', `/contracts-cancel/${id}`),
  runBilling: (id) => request('POST', `/contracts-run-billing/${id}`),

  // Step 2b — User plan requests (app paid → admin creates contract)
  listPlanRequests: (params) => request('GET', '/plan-requests', { params }),
  createContractFromPlanRequest: (id, body) =>
    request('POST', `/plan-requests/${id}/create-contract`, { data: body }),
  rejectPlanRequest: (id, body) =>
    request('POST', `/plan-requests/${id}/reject`, { data: body }),

  // Step 3 — Offers
  listOffers: (params) => request('GET', '/offers', { params }),
  createOffer: (body) => request('POST', '/offers', { data: body }),
  updateOffer: (id, body) => request('PUT', `/offers/${id}`, { data: body }),
  assignOffer: (id, body) => request('POST', `/offers/${id}/assign`, { data: body }),

  // Step 4 — Payment setup
  listPayments: (params) => request('GET', '/payments', { params }),
  getPaymentSetup: (userId) => request('GET', `/users/${userId}/payment-setup`),
  initiatePaymentSetup: (userId, body) =>
    request('POST', `/users/${userId}/payment-setup/initiate`, { data: body }),
  confirmPaymentSetup: (userId, body) =>
    request('POST', `/users/${userId}/payment-setup/confirm`, { data: body }),

  // Step 5 — Subscriptions
  listSubscriptions: (params) => request('GET', '/subscriptions', { params }),
  getSubscription: (id) => request('GET', `/subscriptions/${id}`),
  getSubscriptionUsage: (id) => request('GET', `/subscriptions/${id}/usage`),
  consumeSubscription: (id, body) =>
    request('POST', `/subscriptions/${id}/consume`, { data: body }),
  cancelSubscription: (id) => request('POST', `/subscriptions/${id}/cancel`),
  purchaseAddon: (id, body) =>
    request('POST', `/subscriptions/${id}/addons/purchase`, { data: body }),
  grantAdminTime: (subscriptionId, body) =>
    request('POST', `/subscriptions/${subscriptionId}/admin-time`, { data: body }),

  // Step 6 — Invoices
  listInvoices: (params) => request('GET', '/invoices', { params }),
  getInvoice: (id) => request('GET', `/invoices/${id}`),
  createInvoice: (body) => request('POST', '/invoices', { data: body }),
  issueInvoice: (id) => request('POST', `/invoices/${id}/issue`),
  downloadInvoicePdf: async (id) => {
    const res = await api.request({
      method: 'GET',
      url: prefix(`/invoices/${id}/pdf`),
      responseType: 'blob',
    });
    return res.data;
  },

  // Step 7 — Usage logs
  listUsageLogs: (params) => request('GET', '/usage-logs', { params }),

  // Step 8 — Reports
  reportRevenue: (params) => request('GET', '/reports/revenue', { params }),
  gstSummary: (params) => request('GET', '/reports/gst-summary', { params }),
  reportMrr: (params) => request('GET', '/reports/mrr', { params }),
  reportOutstanding: (params) => request('GET', '/reports/outstanding', { params }),
  reportPipeline: (params) => request('GET', '/reports/contract-pipeline', { params }),
  reportUsageVsQuota: (params) => request('GET', '/reports/usage-vs-quota', { params }),

  // Step 9 — Notifications
  listNotificationLogs: (params) => request('GET', '/notifications/logs', { params }),
  resendNotification: (logId) => request('POST', `/notifications/${logId}/resend`),

  // Step 10 — Audit
  listAuditLogs: (params) => request('GET', '/audit-logs', { params }),
};

/** @deprecated Use legacyCustomerApi — kept for gradual migration */
subscriptionAdminApi.listCustomers = async (params) => {
  const users = await legacyCustomerApi.listUsers();
  const filtered = filterSubscriptionCustomers(users);
  if (params?.purchaseType) {
    return filtered.filter((u) => u.purchaseType === params.purchaseType);
  }
  return filtered;
};

subscriptionAdminApi.createCustomer = legacyCustomerApi.createCustomer;
subscriptionAdminApi.updateCustomer = legacyCustomerApi.updateCustomer;

export function getApiErrorMessage(err) {
  return (
    err?.response?.data?.message ||
    err?.message ||
    'Something went wrong. Please try again.'
  );
}
